import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import logo from "../../../assets/image/logo.png";
import apiCall from "../../../utils/axiosCall";
import { endPoint } from "../../../utils/endPoint";
import {
  clearFlowSession,
  hasGuidelinesAgreement,
  markGuidelinesAgreed,
  readBootstrapState,
  readFlowStage,
  saveFlowStage,
  saveBootstrapState,
} from "./flowStorage";
import "./GuidelinesStyle.css";

const INSTRUCTION_ITEMS = [
  {
    title: "Create a Distraction-Free Zone",
    text: "Ensure you are in a quiet, distraction-free area and maintain eye contact with the camera.",
  },
  {
    title: "Stay on the Interview Screen",
    text: "Do not switch tabs or refresh the screen during the interview. Ensure you stay on this tab throughout the process.",
  },
  {
    title: "Be Aware of Monitoring",
    text: "Your interview will be recorded and reviewed by the hiring manager. Maintain a professional demeanor throughout to leave a positive impression.",
  },
  {
    title: "Position Yourself Clearly",
    text: "Ensure your upper body is clearly visible on camera. Avoid unnecessary body, face, eye, or hand movements, and do not move away from the camera to maximize your job opportunity.",
  },
];

const PERMISSION_ITEMS = [
  { id: "camera", label: "Enable Camera", icon: "camera" },
  { id: "microphone", label: "Enable Microphone", icon: "microphone" },
  { id: "audio", label: "Enable Audio", icon: "volume" },
];

function PermissionIcon({ type }) {
  if (type === "camera") {
    return (
      <svg className='guidelines-permission-icon' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <rect x='2.5' y='4.5' width='11' height='9' rx='1.5' stroke='currentColor' strokeWidth='1.5' />
        <path d='M13.5 7.5L17 5.5V14.5L13.5 12.5' stroke='currentColor' strokeWidth='1.5' strokeLinejoin='round' />
      </svg>
    );
  }

  if (type === "microphone") {
    return (
      <svg className='guidelines-permission-icon' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
        <rect x='7' y='3' width='6' height='10' rx='3' stroke='currentColor' strokeWidth='1.5' />
        <path d='M4.5 10.5C4.5 13.26 6.74 15.5 10 15.5C13.26 15.5 15.5 13.26 15.5 10.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
        <path d='M10 15.5V17.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
      </svg>
    );
  }

  return (
    <svg className='guidelines-permission-icon' viewBox='0 0 20 20' fill='none' aria-hidden='true'>
      <path d='M3.5 8.5V11.5C3.5 12.05 3.95 12.5 4.5 12.5H6.5L9.5 15.5V4.5L6.5 7.5H4.5C3.95 7.5 3.5 7.95 3.5 8.5Z' stroke='currentColor' strokeWidth='1.5' strokeLinejoin='round' />
      <path d='M12.5 7.5C13.4 8.2 14 9.3 14 10.5C14 11.7 13.4 12.8 12.5 13.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
      <path d='M14.5 5.5C16.2 6.8 17.2 8.5 17.2 10.5C17.2 12.5 16.2 14.2 14.5 15.5' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
    </svg>
  );
}

function normalizeBootstrapFromUrl(searchParams, roomIdFromPath) {
  return {
    roomId: roomIdFromPath || "",
    userId: searchParams.get("userId") || "",
    userType: searchParams.get("userType") || "",
    rawUserType: searchParams.get("rawUserType") || "",
    sessionId: searchParams.get("sessionId") || "",
  };
}

function stopStream(stream) {
  if (!stream) {
    return;
  }
  stream.getTracks().forEach((track) => track.stop());
}

export default function Guidelines() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();

  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [bootstrap, setBootstrap] = useState(null);

  const bootFromQuery = useMemo(
    () => normalizeBootstrapFromUrl(searchParams, roomId),
    [roomId, searchParams]
  );

  useEffect(() => {
    const stored = readBootstrapState();
    if (stored && stored.roomId === roomId) {
      setBootstrap(stored);
      return;
    }

    if (bootFromQuery.roomId && bootFromQuery.userId) {
      saveBootstrapState(bootFromQuery);
      setBootstrap(bootFromQuery);
      return;
    }

    setError("Guidelines session is missing. Please verify OTP again.");
  }, [bootFromQuery, roomId]);

  useEffect(() => {
    if (!bootstrap?.roomId || !bootstrap?.userId) {
      return undefined;
    }

    window.history.pushState(null, "", window.location.href);
    const otpRoute = `/external-interview-otp-screen?id=${bootstrap.userId}&room=${bootstrap.roomId}`;
    const handlePopState = () => {
      clearFlowSession(bootstrap.roomId, bootstrap.userId);
      navigate(otpRoute, { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [bootstrap, navigate]);

  useEffect(() => {
    if (!bootstrap?.roomId || !bootstrap?.userId) {
      return;
    }

    const currentStage = readFlowStage(bootstrap.roomId, bootstrap.userId);
    if (currentStage === "interview") {
      const query = new URLSearchParams({
        userId: bootstrap.userId,
        userType: bootstrap.userType,
        sessionId: bootstrap.sessionId || "",
      });
      navigate(`/external-interview-v2/${bootstrap.roomId}?${query.toString()}`, {
        replace: true,
      });
      return;
    }

    if (currentStage === "completed") {
      navigate(
        bootstrap.userType === "interviewer" ? "/feedback/" : "/thank-you/",
        {
          replace: true,
          state: { from: "External-interview" },
        }
      );
      return;
    }

    if (hasGuidelinesAgreement(bootstrap.roomId, bootstrap.userId)) {
      setAgreed(true);
    }
  }, [bootstrap, navigate]);

  const ensureMediaPermission = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    });
    stopStream(stream);
  };

  const recordInterviewTiming = async () => {
    if (!bootstrap?.sessionId || bootstrap?.rawUserType !== "candidate") {
      return;
    }

    const payload = {
      session_id: bootstrap.sessionId,
      interview_timing_id: -1,
      end_time: new Date().toISOString(),
    };

    const roleLabel = (bootstrap.userType || "unknown").toUpperCase();
    console.log(`[${roleLabel}][GUIDELINES_RECORD_TIME_SENT]`, payload);
    const response = await apiCall({
      url: endPoint.EXTERNAL_INTERVIEW_RECORD_INTERVIEW_TIME,
      method: "post",
      data: JSON.stringify(payload),
    });

    const interviewTimingId = response?.result?.interview_timing_id;
    if (interviewTimingId !== undefined && interviewTimingId !== null) {
      localStorage.setItem("interview_timing_id", String(interviewTimingId));
    }
  };

  const handleContinue = async () => {
    setError("");
    if (!agreed) {
      setError("Please agree to the interview guidelines before continuing.");
      return;
    }
    if (!bootstrap?.roomId || !bootstrap?.userId || !bootstrap?.userType) {
      setError("Interview setup data is missing. Please re-open the OTP link.");
      return;
    }

    setLoading(true);
    try {
      await ensureMediaPermission();
      await recordInterviewTiming();

      markGuidelinesAgreed(bootstrap.roomId, bootstrap.userId);
      const roleLabel = (bootstrap.userType || "unknown").toUpperCase();
      console.log(`[${roleLabel}][GUIDELINES_AGREED]`, {
        roomId: bootstrap.roomId,
        userId: bootstrap.userId,
      });
      saveFlowStage(bootstrap.roomId, bootstrap.userId, "interview");

      const query = new URLSearchParams({
        userId: bootstrap.userId,
        userType: bootstrap.userType,
        sessionId: bootstrap.sessionId || "",
      });

      navigate(`/external-interview-v2/${bootstrap.roomId}?${query.toString()}`, {
        replace: true,
      });
    } catch (actionError) {
      console.log("[EXTERNAL_INTERVIEW_GUIDELINES_ERROR]", actionError);
      setError("Camera, microphone, and fullscreen permission are required to continue.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='guidelines-page'>
      <header className='guidelines-header'>
        <div className='guidelines-header-inner'>
          <img src={logo} alt='Resourceful' className='guidelines-logo' />
        </div>
      </header>

      <main className='guidelines-main'>
        <div className='guidelines-card'>
          <div className='guidelines-card-content'>
            <div className='guidelines-card-body'>
              <h1 className='guidelines-title'>Guidelines</h1>

              <div className='guidelines-sections'>
                <section className='guidelines-section'>
                  <div className='guidelines-section-header'>
                    <h2 className='guidelines-section-title'>Instructions</h2>
                    <p className='guidelines-section-intro'>
                      Complete your video screening successfully and make a positive impression on
                      the hiring manager. Please follow these tips and instructions:
                    </p>
                  </div>

                  <div className='guidelines-items'>
                    {INSTRUCTION_ITEMS.map((item) => (
                      <article key={item.title} className='guidelines-item'>
                        <h3 className='guidelines-item-title'>{item.title}</h3>
                        <p className='guidelines-item-text'>{item.text}</p>
                      </article>
                    ))}
                  </div>
                </section>

                <section className='guidelines-section'>
                  <div className='guidelines-section-header'>
                    <h2 className='guidelines-section-title'>Grant Permissions</h2>
                    <p className='guidelines-section-intro'>
                      We require your permission to access the following tools for the interview.
                      These permissions must remain enabled until the interview ends:
                    </p>
                  </div>

                  <div className='guidelines-permissions'>
                    {PERMISSION_ITEMS.map((item) => (
                      <div key={item.id} className='guidelines-permission'>
                        <PermissionIcon type={item.icon} />
                        <span className='guidelines-permission-label'>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </div>

            <label htmlFor='guidelines-agree' className='guidelines-agree-label'>
              <input
                id='guidelines-agree'
                type='checkbox'
                checked={agreed}
                onChange={(event) => setAgreed(event.target.checked)}
              />
              I agree and I am ready to continue.
            </label>

            {error ? <p className='guidelines-error'>{error}</p> : null}

            <button
              type='button'
              className='guidelines-grant-button'
              onClick={handleContinue}
              disabled={loading || !agreed}
            >
              {loading ? "Preparing..." : "Grant Camera & Microphone Access"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
